import { html, render } from 'lit/html.js'
import { get } from 'object-path'
import { Scenario } from './textRiskScenario.js'
import scores from './provider.scores.json'
import words from './language.risk.json'
import { provider } from './provider'

// -------------------------------------------------------------------------------
// Create bewertung and empfehlung texts depending on risk points and risk factors
// -------------------------------------------------------------------------------
export default function ( xeon, lang ) {

  const data = provider( xeon )
  const rules = data.rules()
  const drugs = data.drugNamesById( lang )

  // compute the risk points and get the missing and risk factors
  const { riskPoints, risks, riskPointsUnknown, risksUnknown } = data.computeRisk()

  // Select risk scenario for showing appropriate texts
  const scenario = new Scenario( drugs, riskPoints, risks, riskPointsUnknown, risksUnknown )
  const { bewertungText, empfehlungText } = scenario.selectScenario()

  // Initialize the chapter classes
  const bewertung = new Bewertung( bewertungText, words[ lang ], risks, drugs, riskPoints, riskPointsUnknown, risksUnknown )
  const empfehlung = new Empfehlung( empfehlungText, words[ lang ], risks, drugs, riskPoints, riskPointsUnknown, risksUnknown )

  //style="color:#02578a"
  return {
    empfehlung: empfehlung.createText(),
    bewertung: bewertung.createText(),
    riskPoints
  }

}


// ----------------------------------------------------------------
// A base class for chapters, like Bewertung and Empfehlung
// TODO: at the moment is too specific of xeon-qtc,
// needs to be adapted when we work on other groups
// ----------------------------------------------------------------
class Chapter {
  constructor( scenario, words, risks, drugs, riskPoints, riskPointsUnknown, risksUnknown ) {
    this.scenario = scenario
    this.words = words
    this.replacer = new Replacer( words )
    this.text = words[ scenario ] || ''
    this.risks = risks
    this.drugs = drugs
    this.riskPoints = riskPoints || null
    this.riskPointsUnknown = riskPointsUnknown
    this.risksUnknown = risksUnknown

  }
  createText() {
    // Special case when riskPoints == 1. Punkte -> Punkt
    if ( this.riskPoints == 1 ) {
      this.text = this.text.replace( 'X Punkten', 'X Punkt' )
    }
    // Replace with the amount of risk points
    this.text = this.text.replace( ' X ', ` ${this.riskPoints} ` )

    return this.text
  }
}

// ------------------------
// Bewertung chapter of qtc
// ------------------------
class Bewertung extends Chapter {
  constructor( scenario, words, risks, drugs, riskPoints, riskPointsUnknown, risksUnknown ) {
    super( scenario, words, risks, drugs, riskPoints, riskPointsUnknown, risksUnknown )
  }
  createText() {
    const nonDrugRisks = this.risks.filter( risk => typeof risk.drug === 'undefined' )
    this.text = this.replacer.replaceDrug( this.text, this.drugs )
    this.text = this.replacer.replaceRFS( this.text, nonDrugRisks )

    // Special case when riskPoints == 1. Punkte -> Punkt
    if ( this.riskPoints == 1 ) {
      this.text = this.text.replace( 'X Punkten', 'X Punkt' )
    }
    // Replace with the amount of risk points
    this.text = this.text.replace( ' X ', ` ${this.riskPoints} ` )

    // Replace with the amount of risk points + unknown risk points
    this.text = this.text.replace( 'XMAX', ` ${this.riskPoints + this.riskPointsUnknown} ` )

    // Replace with the number of unknown risk factores
    this.text = this.text.replace( ' UF ', ` ${this.risksUnknown.length} ` )

    return this.text
  }
}

// -------------------------
// Empfehlung chapter of qtc
// -------------------------
class Empfehlung extends Chapter {
  constructor( scenario, words, risks, drugs, riskPoints, riskPointsUnknown, risksUnknown ) {
    super( scenario, words, risks, drugs, riskPoints, riskPointsUnknown, risksUnknown )
  }
  createText() {
    this.text = this.replacer.replaceDrug( this.text, this.drugs )
    const regex_hypokal = new RegExp( this.words.hypokal, "ig" )
    const regex_kal = new RegExp( this.words.kalium, "ig" )

    // is kalium or/and Calcium risk factor(s)
    const isKaliumInRisk = this.risks.some( risk => risk.id === 'kalium' )
    const isCalciumInRisk = this.risks.some( risk => risk.id === 'calcium' )

    let hypoRfs, elektRfs
    // Elektrolyts are not risk factors
    if ( !isKaliumInRisk && !isCalciumInRisk ) {
      return this.text
    }
    // Both Hypokaliämie and Hypokalzämie are risk factors
    else if ( isKaliumInRisk && isCalciumInRisk ) {
      hypoRfs = [ this.words.hypokal, this.words.hypokalz ]
      elektRfs = [ this.words.kalium, this.words.calcium ]
    }
    // Only Hypokaliämie is risk factor
    else if ( isKaliumInRisk ) {
      hypoRfs = [ this.words.hypokal ]
      elektRfs = [ this.words.kalium ]
    }
    // Only Hypokalzämie is risk factor
    else {
      hypoRfs = [ this.words.hypokalz ]
      elektRfs = [ this.words.calcium ]
    }
    this.text = this.replacer.replaceText( this.text, hypoRfs, regex_hypokal )
    this.text = this.replacer.replaceText( this.text, elektRfs, regex_kal )
    return this.text
  }
}


class Replacer {
  constructor( words ) {
    this.words = words
  }
  // ----------------------------------------------------------
  // Replaces the template drug names with the real ones
  // Example:
  //   Input:
  //     text: "Bla Bla Simvastatin und Clarithromycin bla bla"
  //     ids: ['amiodaron', 'ibuprofen', paracetamol]
  //   Returns:
  //     "Bla Bla Amiodaron, Ibuprofen und Paracetamol bla bla"
  // ----------------------------------------------------------
  replaceDrug( text, drug ) {
    // Replacement regex
    const regex = new RegExp( `${this.words.substance1}.+${this.words.substance2}`, "i" )
    // Uppercase the name of the first drug if the text starts with drugs
    let drugs = Object.values( drug )
    if ( text.search( regex ) === 0 ) {
      drugs[ 0 ] = drugs[ 0 ].charAt( 0 ).toUpperCase() + drugs[ 0 ].slice( 1 )
    }
    drugs = drugs.map( dr => `<b class=d>${dr}</b>` )

    return this.replaceText( text, drugs, regex )
  }

  // ----------------------------------------------------------
  // Replaces the dummy risk factors with the real ones
  // Example:
  //   Input:
  //     text: "Bla Bla RFS und RFS bla bla"
  //     rfs: ['qtc', 'age', 'gen']
  //   Returns:
  //     "Bla Bla QT-Zeit länger als 470 ms, Alter zwischen 65
  //      und 85 Jahre und weibliches Geschlecht bla bla"
  // ----------------------------------------------------------
  replaceRFS( text, rfs ) {

    let risks = rfs.reduce( ( acc, cur ) => {
      const word = `${cur.group}_${cur.id}_${cur.val}`
      acc.push( this.words[ word ] )
      return acc
    }, [] )
    risks = risks.map( rf => `<b class=r>${rf}</b>` )


    // Replacement regex
    const regex = new RegExp( `RFS.+RFS` )

    return this.replaceText( text, risks, regex )
  }

  // --------------------------------------------------------------------
  // Replace text with comma separated string consisting of arr elements.
  // End the sentence with und last_element
  // --------------------------------------------------------------------
  replaceText( text, arr, regex ) {
    // What to replace with
    let repl = '????'
    if ( arr.length >= 2 ) {
      repl = `${arr.slice(0, -1).join(', ')} ${this.words['und']} ${arr.slice(-1)[0]}`
    } else if ( arr.length === 1 ) {
      repl = arr[ 0 ]
    }
    return text.replace( regex, repl )
  }
}